import { useEffect, useState } from "react";
import {
  Users,
  FileText,
  CreditCard,
  Sparkles,
  TrendingUp,
  Eye,
  Heart,
  ArrowUpRight,
  Download,
  Plus,
  ShieldCheck,
  Activity,
  Share2,
} from "lucide-react";
import {
  Card,
  Stat,
  List,
  ListItem,
  Button,
  BentoGrid,
  PageHeader,
  Badge,
  Empty,
  Skeleton,
} from "@/components/ui";
import { adminApi } from "@/lib/adminApi";
import { cn } from "@/lib/utils";

interface DashboardStats {
  totalUsers: number;
  activeProposals: number;
  totalRevenue: number;
  aiGenerations: number;
}

interface RecentProposal {
  id: string;
  title: string;
  proposeeName: string;
  status: string;
  creatorName: string | null;
  creatorEmail: string;
  createdAt: string;
}

interface RecentSignup {
  id: string;
  name: string | null;
  email: string;
  tier: string;
  createdAt: string;
}

const STATUS_VARIANT = {
  DRAFT: "warning",
  PUBLISHED: "success",
  SENT: "info",
  ACCEPTED: "pink",
  DECLINED: "error",
} as const;

const TIER_VARIANT = {
  FREE: "neutral",
  PRO: "pink",
} as const;

const TIER_LEADING_TINT = {
  FREE: "yellow",
  PRO: "pink",
} as const;

export default function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentProposals, setRecentProposals] = useState<RecentProposal[]>([]);
  const [recentSignups, setRecentSignups] = useState<RecentSignup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchDashboard() {
      try {
        const data = await adminApi.post<{
          success: boolean;
          stats: DashboardStats;
          recentProposals: RecentProposal[];
          recentSignups: RecentSignup[];
        }>("/admin/stats/dashboard", {});

        if (data.success) {
          setStats(data.stats);
          setRecentProposals(data.recentProposals);
          setRecentSignups(data.recentSignups);
        } else {
          setError("Failed to load dashboard");
        }
      } catch (err) {
        console.error("Failed to fetch dashboard:", err);
        setError("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    }
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <DashboardSkeleton />;

  if (error || !stats) {
    return (
      <div>
        <PageHeader eyebrow="Overview" title="Dashboard" subtitle="Operational overview." />
        <Card tint="rose">
          <p className="text-sm text-[var(--color-red)]">
            {error || "Failed to load dashboard"}
          </p>
        </Card>
      </div>
    );
  }

  const weeklyChart = [38, 56, 72, 61, 88, 92, 78];
  const max = Math.max(...weeklyChart);
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <div>
      <PageHeader
        eyebrow="Overview"
        title="Dashboard"
        subtitle="Welcome back — here's what's happening with Merryflix."
        actions={
          <>
            <Button variant="secondary">
              <Download className="h-4 w-4" />
              Export
            </Button>
            <Button variant="primary">
              <Plus className="h-4 w-4" />
              New
            </Button>
          </>
        }
      />

      <BentoGrid>
        <div className="bento-col-2 md:bento-col-3 lg:bento-col-3">
          <Stat
            value={stats.totalUsers.toLocaleString()}
            label="Total Users"
            icon={<Users className="h-4 w-4" />}
            tint="pink"
            delta="+12.4%"
            deltaDir="up"
          />
        </div>
        <div className="bento-col-2 md:bento-col-3 lg:bento-col-3">
          <Stat
            value={stats.activeProposals.toLocaleString()}
            label="Active Proposals"
            icon={<FileText className="h-4 w-4" />}
            tint="blue"
            delta="+8.2%"
            deltaDir="up"
          />
        </div>
        <div className="bento-col-2 md:bento-col-3 lg:bento-col-3">
          <Stat
            value={`$${(stats.totalRevenue / 100).toLocaleString()}`}
            label="Revenue"
            icon={<CreditCard className="h-4 w-4" />}
            tint="orange"
            delta="+24.6%"
            deltaDir="up"
          />
        </div>
        <div className="bento-col-2 md:bento-col-3 lg:bento-col-3">
          <Stat
            value={stats.aiGenerations.toLocaleString()}
            label="AI Generations"
            icon={<Sparkles className="h-4 w-4" />}
            tint="purple"
            delta="+5.1%"
            deltaDir="up"
          />
        </div>

        <div className="bento-col-4 md:bento-col-6 lg:bento-col-8">
          <Card tint="blue" className="min-h-[300px]">
            <div className="flex items-center justify-between">
              <div>
                <div className="card-title">Weekly engagement</div>
                <div className="card-subtitle">Last 7 days · proposals viewed</div>
              </div>
              <div className="flex items-center gap-1 text-[13px] font-semibold text-[#6EE7A0]">
                <TrendingUp className="h-3.5 w-3.5" />
                +18.2%
              </div>
            </div>

            <div className="flex h-52 items-end gap-3 pt-4">
              {weeklyChart.map((v, i) => (
                <div
                  key={i}
                  className="flex h-full flex-1 flex-col items-center justify-end gap-2"
                >
                  <div
                    className={cn(
                      "w-full",
                      i === weeklyChart.length - 1 ? "bar is-active" : "bar",
                    )}
                    style={{ height: `${(v / max) * 100}%` }}
                  />
                  <span className="text-[10px] font-medium uppercase tracking-wider text-[var(--color-tertiary-label)]">
                    {days[i]}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="bento-col-4 md:bento-col-6 lg:bento-col-4">
          <Card tint="green" className="min-h-[300px]">
            <div className="flex items-center justify-between">
              <div>
                <div className="card-title">Yes rate</div>
                <div className="card-subtitle">Proposals accepted</div>
              </div>
              <span className="stat-icon tint-green">
                <Heart className="h-4 w-4" />
              </span>
            </div>

            <div className="flex flex-1 items-center gap-5 pt-3">
              <div className="relative h-32 w-32 shrink-0">
                <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
                  <circle
                    cx="50"
                    cy="50"
                    r="42"
                    stroke="rgba(255,255,255,0.08)"
                    strokeWidth="10"
                    fill="none"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="42"
                    stroke="#30D158"
                    strokeWidth="10"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 42 * 0.82} ${2 * Math.PI * 42}`}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="text-[28px] font-bold tracking-tight text-white">
                    82%
                  </div>
                  <div className="text-[10px] font-medium uppercase tracking-wider text-[var(--color-tertiary-label)]">
                    yes
                  </div>
                </div>
              </div>

              <div className="space-y-2.5">
                <div className="flex items-center justify-between gap-6">
                  <span className="text-[12.5px] text-[var(--color-secondary-label)]">Accepted</span>
                  <span className="text-[14px] font-semibold text-[#6EE7A0]">328</span>
                </div>
                <div className="flex items-center justify-between gap-6">
                  <span className="text-[12.5px] text-[var(--color-secondary-label)]">Declined</span>
                  <span className="text-[14px] font-semibold text-[#FF8A82]">72</span>
                </div>
                <div className="flex items-center justify-between gap-6">
                  <span className="text-[12.5px] text-[var(--color-secondary-label)]">Pending</span>
                  <span className="text-[14px] font-semibold text-[#FFC25C]">14</span>
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div className="bento-col-4 md:bento-col-6 lg:bento-col-7">
          <Card className="min-h-[320px] !p-0 overflow-hidden">
            <div className="flex items-center justify-between p-5 pb-2">
              <div>
                <div className="card-title">Recent proposals</div>
                <div className="card-subtitle">Latest activity from creators</div>
              </div>
              <button className="btn btn-tertiary btn-sm cursor-pointer">
                View all
                <ArrowUpRight className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="px-2 pb-2">
              {recentProposals.length === 0 ? (
                <Empty
                  icon={<FileText className="h-5 w-5" />}
                  title="No proposals yet"
                  body="Once users start creating proposals they'll appear here."
                />
              ) : (
                <List className="!rounded-2xl">
                  {recentProposals.slice(0, 6).map((p, i) => (
                    <ListItem
                      key={p.id}
                      noSeparator={i === 0}
                      title={<span className="font-medium">{p.title}</span>}
                      subtitle={`${p.proposeeName} · ${p.creatorName || p.creatorEmail}`}
                      trailing={
                        <Badge tone={STATUS_VARIANT[p.status as keyof typeof STATUS_VARIANT] ?? "neutral"}>
                          {p.status}
                        </Badge>
                      }
                    />
                  ))}
                </List>
              )}
            </div>
          </Card>
        </div>

        <div className="bento-col-4 md:bento-col-6 lg:bento-col-5">
          <Card className="min-h-[320px] !p-0 overflow-hidden">
            <div className="flex items-center justify-between p-5 pb-2">
              <div>
                <div className="card-title">Recent signups</div>
                <div className="card-subtitle">Newest users</div>
              </div>
              <span className="stat-icon tint-blue">
                <Users className="h-4 w-4" />
              </span>
            </div>

            <div className="px-2 pb-2">
              {recentSignups.length === 0 ? (
                <Empty
                  icon={<Users className="h-5 w-5" />}
                  title="No signups yet"
                  body="New signups will appear here as they happen."
                />
              ) : (
                <List className="!rounded-2xl">
                  {recentSignups.slice(0, 5).map((s, i) => (
                    <ListItem
                      key={s.id}
                      noSeparator={i === 0}
                      leading={
                        <span className="text-[13px] font-semibold">
                          {s.name?.charAt(0).toUpperCase() || "?"}
                        </span>
                      }
                      leadingTint={TIER_LEADING_TINT[s.tier as keyof typeof TIER_LEADING_TINT] ?? "blue"}
                      title={<span className="font-medium">{s.name || "Anonymous"}</span>}
                      subtitle={s.email}
                      trailing={
                        <Badge tone={TIER_VARIANT[s.tier as keyof typeof TIER_VARIANT] ?? "neutral"}>
                          {s.tier}
                        </Badge>
                      }
                    />
                  ))}
                </List>
              )}
            </div>
          </Card>
        </div>

        <div className="bento-col-4 md:bento-col-4 lg:bento-col-4">
          <Card tint="orange" interactive className="min-h-[170px] cursor-pointer">
            <div className="flex items-center justify-between">
              <div className="card-title">Live now</div>
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FFC25C] opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#FFC25C]" />
              </span>
            </div>
            <div className="flex items-end gap-2">
              <div className="card-value">27</div>
              <div className="pb-1.5 text-[12px] text-[var(--color-secondary-label)]">viewers</div>
            </div>
            <div className="flex items-center gap-1.5 text-[11.5px] text-[var(--color-tertiary-label)]">
              <Eye className="h-3.5 w-3.5" />
              Watching proposals right now
            </div>
          </Card>
        </div>

        <div className="bento-col-4 md:bento-col-4 lg:bento-col-4">
          <Card tint="pink" interactive className="min-h-[170px] cursor-pointer">
            <div className="card-title">Hottest moment</div>
            <div className="card-subtitle">Most-shared proposal (24h)</div>
            <div className="mt-1 truncate text-[15px] font-semibold text-white">
              &ldquo;Sarah&rsquo;s Birthday Surprise&rdquo;
            </div>
            <div className="mt-1 flex items-center gap-3 text-[11.5px]">
              <span className="text-[#FF7A95]">💕 142 yes</span>
              <span className="text-[var(--color-tertiary-label)]">· 38 shares</span>
            </div>
          </Card>
        </div>

        <div className="bento-col-4 md:bento-col-4 lg:bento-col-4">
          <Card className="min-h-[170px]">
            <div className="card-title flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5 text-[#6EE7A0]" />
              System status
            </div>
            <div className="mt-3 space-y-2 text-[12px]">
              {[
                { label: "API", status: "operational" },
                { label: "AI generation", status: "operational" },
                { label: "Payments", status: "degraded" },
                { label: "Email delivery", status: "operational" },
              ].map((s) => (
                <div key={s.label} className="flex items-center justify-between">
                  <span className="text-[var(--color-secondary-label)]">{s.label}</span>
                  <Badge
                    variant={
                      s.status === "operational"
                        ? "success"
                        : s.status === "degraded"
                          ? "warning"
                          : "error"
                    }
                  >
                    {s.status}
                  </Badge>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="bento-col-4 md:bento-col-12 lg:bento-col-12">
          <Card className="!p-0 overflow-hidden">
            <div className="flex items-center justify-between p-5 pb-2">
              <div>
                <div className="card-title flex items-center gap-2">
                  <Activity className="h-3.5 w-3.5 text-[#7AB8FF]" />
                  Recent activity
                </div>
                <div className="card-subtitle">Live events from your platform</div>
              </div>
              <Button variant="tertiary" size="sm">
                View all
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Button>
            </div>

            <div className="px-2 pb-2">
              <List className="!rounded-2xl">
                {[
                  { who: "James", action: "published", what: "Sarah's Birthday Surprise", when: "2m ago", tint: "pink" as const, icon: <Heart className="h-3.5 w-3.5" /> },
                  { who: "Sophie", action: "shared", what: "The Long Distance Question", when: "12m ago", tint: "blue" as const, icon: <Share2 className="h-3.5 w-3.5" /> },
                  { who: "Marcus", action: "got a Yes on", what: "Our First Anniversary", when: "34m ago", tint: "green" as const, icon: <Sparkles className="h-3.5 w-3.5" /> },
                  { who: "Yuki", action: "edited", what: "Midnight Garden Proposal", when: "1h ago", tint: "purple" as const, icon: <FileText className="h-3.5 w-3.5" /> },
                  { who: "Tom", action: "upgraded to Pro", what: "", when: "2h ago", tint: "gold" as const, icon: <CreditCard className="h-3.5 w-3.5" /> },
                ].map((row, i) => (
                  <ListItem
                    key={i}
                    noSeparator={i === 0}
                    leading={row.icon}
                    leadingTint={row.tint as any}
                    title={
                      <span>
                        <span className="font-semibold">{row.who}</span>{" "}
                        <span className="text-[var(--color-secondary-label)] font-normal">
                          {row.action}
                        </span>{" "}
                        {row.what && (
                          <span className="font-medium text-white">{row.what}</span>
                        )}
                      </span>
                    }
                    trailing={
                      <span className="text-[12px] text-[var(--color-tertiary-label)]">
                        {row.when}
                      </span>
                    }
                  />
                ))}
              </List>
            </div>
          </Card>
        </div>
      </BentoGrid>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div>
      <PageHeader eyebrow="Overview" title="Dashboard" subtitle="Operational overview." />
      <BentoGrid>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bento-col-2 md:bento-col-3 lg:bento-col-3">
            <Card>
              <Skeleton width="40%" height={12} />
              <Skeleton width="60%" height={32} className="mt-2" />
            </Card>
          </div>
        ))}
      </BentoGrid>
    </div>
  );
}